import { useEffect, useRef, useSyncExternalStore } from "react";
import { fetchHostsStatus } from "./hostStatusService";

// Store em escopo de módulo (singleton): várias abas de circuito (edição e preview) ficam
// montadas ao mesmo tempo (ver CLAUDE.md), cada uma podendo ter nodes com host vinculado.
// Em vez de cada diagrama buscar/fazer polling do próprio status, cada instância registra
// aqui os hostIds que precisa observar (só enquanto estiver na aba ativa); o store une os
// hostIds de todos os consumidores registrados e faz uma única requisição em lote por ciclo,
// evitando multiplicar chamadas à API do Zabbix por aba.
const POLL_INTERVAL_MS = 60000;

let cache = {}; // { [hostId]: { snmp, ping, status } }
const listeners = new Set();
const wantedByConsumer = new Map(); // consumerId -> Set<hostId>
let inFlight = null;
let pollTimer = null;

const notify = () => listeners.forEach((listener) => listener());

// União dos hostIds atualmente desejados por qualquer consumidor registrado
const getAllWantedHostIds = () => {
  const todos = new Set();
  wantedByConsumer.forEach((ids) => ids.forEach((id) => todos.add(id)));
  return [...todos];
};

// Busca o status de um lote de hostIds; chamadas concorrentes reaproveitam a requisição em
// andamento e, ao final dela, reavaliam se algum id pedido continua sem resposta em cache
const runFetch = (hostIds) => {
  if (!hostIds?.length) return;
  if (inFlight) {
    inFlight.then(() => {
      const faltando = hostIds.filter((id) => !(id in cache));
      if (faltando.length) runFetch(faltando);
    });
    return;
  }
  inFlight = fetchHostsStatus(hostIds)
    .then((resultado) => {
      cache = { ...cache, ...resultado };
      notify();
    })
    .catch((error) => {
      console.error("Erro ao buscar status de hosts vinculados ao diagrama:", error);
    })
    .finally(() => {
      inFlight = null;
    });
};

// Garante um único intervalo compartilhado rodando enquanto houver ao menos um hostId
// desejado por algum consumidor; para sozinho quando a união fica vazia
const ensurePolling = () => {
  if (pollTimer) return;
  pollTimer = setInterval(() => {
    const hostIds = getAllWantedHostIds();
    if (!hostIds.length) {
      clearInterval(pollTimer);
      pollTimer = null;
      return;
    }
    runFetch(hostIds);
  }, POLL_INTERVAL_MS);
};

export const subscribeHostStatus = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getHostStatusSnapshot = () => cache;

// Registra (substituindo o registro anterior) o conjunto de hostIds que uma instância de
// diagrama precisa observar. Hosts ainda não vistos em cache disparam uma busca imediata,
// sem esperar o próximo ciclo do polling compartilhado.
export const registerHostStatusWanted = (consumerId, hostIds = []) => {
  wantedByConsumer.set(consumerId, new Set(hostIds));
  const novos = hostIds.filter((id) => !(id in cache));
  if (novos.length) runFetch(novos);
  if (getAllWantedHostIds().length) ensurePolling();
};

// Remove o consumidor do registro (desmontou ou saiu da aba ativa) — para o polling
// compartilhado quando nenhum consumidor mais precisa de status algum
export const unregisterHostStatusWanted = (consumerId) => {
  wantedByConsumer.delete(consumerId);
  if (!getAllWantedHostIds().length && pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

// Hook compartilhado por DiagramaCanvas (edição) e DiagramaEquipamentosPreview (visualização):
// registra os hostIds vinculados a nodes deste diagrama enquanto `active` for verdadeiro (aba
// visível) e devolve o cache atual de status, atualizado a cada notificação do store
// compartilhado. `active` falso (aba oculta) remove o registro, sem gerar tráfego de fundo.
export const useHostStatus = (hostIds = [], active = true) => {
  const consumerIdRef = useRef(null);
  if (!consumerIdRef.current) {
    consumerIdRef.current = `host-status-${Math.random().toString(36).slice(2)}`;
  }

  const chaveHostIds = (hostIds || []).join(",");

  useEffect(() => {
    const consumerId = consumerIdRef.current;
    if (!active || !chaveHostIds) {
      unregisterHostStatusWanted(consumerId);
      return undefined;
    }
    registerHostStatusWanted(consumerId, chaveHostIds.split(","));
    return () => unregisterHostStatusWanted(consumerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, chaveHostIds]);

  return useSyncExternalStore(subscribeHostStatus, getHostStatusSnapshot);
};
